import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'

import {
  applyMarketYoyGrowthEdits,
  buildMarketYoyGrowthEdits,
  getInitialExpandedOfferings,
  IMarketYoyGrowthEditTarget,
} from '../lib'

import { requestApplyYoyGrowthPlanningChanges } from './thunks/requestApplyYoyGrowthPlanningChanges'
import { requestApplyYoyGrowthPlanningDetailsChanges } from './thunks/requestApplyYoyGrowthPlanningDetailsChanges'
import { requestMarketYoyGrowthExpectationsData } from './thunks/requestMarketYoyGrowthExpectationsData'
import { IMarketYoyGrowthExpectationState } from './types'

const initialState: IMarketYoyGrowthExpectationState = {
  planningDetails: [],
  planning: {
    entryId: -1,
    uid: '',
    created: '',
    createdBy: '',
    modified: '',
    modifiedBy: '',
    fYear: -1,
    status: 'Draft',
    isLocked: true,
  },
  userInfo: {
    market: '',
    userId: '',
    viewType: 'WorldWide',
  },
  geoMarkets: [],
  categories: [],
  log: [],
  expandedOfferings: [],
  edits: {},
  selectedFYear: null,
  isLoading: false,
  isSaving: false,
  error: null,
  saveError: null,
}

const marketYoyGrowthExpectationSlice = createSlice({
  name: 'marketYoyGrowthExpectation',
  initialState,
  reducers: {
    growthValueEdited(state, action: PayloadAction<IMarketYoyGrowthEditTarget>) {
      state.edits = buildMarketYoyGrowthEdits(
        state.planningDetails,
        state.categories,
        state.geoMarkets,
        state.planning,
        state.edits,
        action.payload
      )
    },
    growthEditsDiscarded(state) {
      state.edits = {}
    },
    offeringExpandToggled(state, action: PayloadAction<ProgramTypes>) {
      const offering = action.payload

      state.expandedOfferings = state.expandedOfferings.includes(offering)
        ? state.expandedOfferings.filter((existing) => existing !== offering)
        : [...state.expandedOfferings, offering]
    },
    fYearSelected(state, action: PayloadAction<number>) {
      state.selectedFYear = action.payload
    },
  },
  extraReducers(builder) {
    builder
      .addCase(requestMarketYoyGrowthExpectationsData.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(requestMarketYoyGrowthExpectationsData.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message ?? 'Failed to fetch market YoY growth expectations'
      })
      .addCase(requestMarketYoyGrowthExpectationsData.fulfilled, (state, action) => {
        state.isLoading = false
        state.planningDetails = action.payload.planningDetails
        state.planning = action.payload.planning
        state.userInfo = action.payload.userInfo
        state.geoMarkets = action.payload.geoMarkets
        state.categories = action.payload.categories
        state.log = action.payload.log
        state.expandedOfferings = getInitialExpandedOfferings(action.payload.planningDetails)
        state.edits = {}
        state.error = null
        state.saveError = null
      })
      .addCase(requestApplyYoyGrowthPlanningDetailsChanges.pending, (state) => {
        state.isSaving = true
        state.saveError = null
      })
      .addCase(requestApplyYoyGrowthPlanningDetailsChanges.rejected, (state, action) => {
        state.isSaving = false
        state.saveError = action.error.message ?? 'Failed to save market YoY growth expectations'
      })
      .addCase(requestApplyYoyGrowthPlanningDetailsChanges.fulfilled, (state) => {
        // The SP response is opaque, so fold the saved edits into the records
        // client-side instead of refetching.
        state.planningDetails = applyMarketYoyGrowthEdits(state.planningDetails, state.edits)
        state.edits = {}
        state.isSaving = false
      })
      .addCase(requestApplyYoyGrowthPlanningChanges.pending, (state) => {
        state.isSaving = true
        state.saveError = null
      })
      .addCase(requestApplyYoyGrowthPlanningChanges.rejected, (state, action) => {
        state.isSaving = false
        state.saveError =
          action.error.message ?? 'Failed to update market YoY growth planning status'
      })
      .addCase(requestApplyYoyGrowthPlanningChanges.fulfilled, (state) => {
        if (state.planning) {
          state.planning.status = 'Published'
          state.planning.modified = new Date().toISOString()
        }
        state.isSaving = false
      })
  },
})

export const { growthValueEdited, growthEditsDiscarded, offeringExpandToggled, fYearSelected } =
  marketYoyGrowthExpectationSlice.actions

export const marketYoyGrowthExpectationReducer = marketYoyGrowthExpectationSlice.reducer
